<template>
  <div class="mods-filters">
    <div class="mods-filters__head">
      <div class="mods-filters__icon">
        <v-icon color="white" small>mdi-filter-variant</v-icon>
      </div>
      <div>
        <div class="mods-filters__title">Фильтры</div>
        <div class="mods-filters__subtitle">Сужайте выбор домофонов</div>
      </div>
    </div>

    <div class="mods-filters__section">
      <div class="mods-filters__section-label">
        <v-icon x-small class="mr-1">mdi-map-search</v-icon>
        Поиск и территория
      </div>
      <v-text-field
        v-model="local.id"
        label="ID домофона"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-identifier"
        class="mb-3 border-rounding"
        @keyup.enter="onSearch"
      />
      <v-combobox
        v-model="local.address"
        :items="addressSuggestions"
        label="Адрес"
        outlined
        dense
        hide-details
        clearable
        hide-no-data
        prepend-inner-icon="mdi-magnify"
        class="mb-3 border-rounding"
        @update:search-input="onAddressSearchInput"
        @keyup.enter="onSearch"
      />
      <v-autocomplete
        v-model="local.region_id"
        label="Район"
        :items="regions"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details
        clearable
        multiple
        small-chips
        deletable-chips
        prepend-inner-icon="mdi-home-city-outline"
        class="mb-3 border-rounding"
      />
      <v-autocomplete
        v-model="local.mto_sector_id"
        label="Участок МТО"
        :items="mtoSectors"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details
        clearable
        multiple
        small-chips
        deletable-chips
        prepend-inner-icon="mdi-map-marker-radius"
        no-data-text="Участки не найдены"
        :loading="mtoSectorsLoading"
        class="mb-3 border-rounding"
      />
    </div>

    <div class="mods-filters__section">
      <div class="mods-filters__section-label">
        <v-icon x-small class="mr-1">mdi-office-building</v-icon>
        Организации
      </div>
      <v-autocomplete
        v-model="local.management_company_id"
        label="УК"
        :items="managementCompanies"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-home-account"
        class="mb-3 border-rounding"
      />
      <v-autocomplete
        v-model="local.management_group_id"
        label="Компания-собственник"
        :items="managementsGroup"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-account-multiple"
        class="mb-3 border-rounding"
      />
      <v-autocomplete
        v-model="local.agent_id"
        label="Агент"
        :items="agents"
        item-text="user.name"
        item-value="id"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-account-search"
        class="mb-3 border-rounding"
      />
    </div>

    <div class="mods-filters__section">
      <div class="mods-filters__section-label">
        <v-icon x-small class="mr-1">mdi-chip</v-icon>
        Устройство
      </div>
      <v-autocomplete
        v-model="local.model_id"
        label="Модель"
        :items="models"
        item-text="name"
        item-value="id"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-cog-outline"
        class="mb-3 border-rounding"
      />
      <v-select
        v-model="local.type"
        label="Тип"
        :items="typeItems"
        item-text="text"
        item-value="value"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-doorbell-video"
        class="mb-3 border-rounding"
      />
      <v-select
        v-model="local.physical_status"
        label="Физ. статус"
        :items="physicalStatusItems"
        item-text="text"
        item-value="value"
        outlined
        dense
        hide-details
        clearable
        prepend-inner-icon="mdi-checkbox-marked-circle-outline"
        class="mb-4 border-rounding"
      />
    </div>

    <v-chip
      v-if="local.bbox"
      small
      label
      color="light-blue lighten-5"
      class="mb-3"
      close
      @click:close="clearBbox"
    >
      <v-icon left x-small color="primary">mdi-selection-drag</v-icon>
      Выделение на карте
    </v-chip>

    <v-btn
      color="primary"
      class="text-none mb-2"
      block
      rounded
      elevation="0"
      @click="onSearch"
    >
      <v-icon left small>mdi-magnify</v-icon>
      Найти
    </v-btn>
    <v-btn
      class="text-none"
      block
      rounded
      outlined
      color="primary"
      @click="reset"
    >
      <v-icon left small>mdi-filter-off-outline</v-icon>
      Сбросить фильтры
    </v-btn>
  </div>
</template>

<script>
const ADDRESS_HISTORY_KEY = 'massOpenDoorSound.addressHistory';
const ADDRESS_HISTORY_LIMIT = 15;

function loadAddressHistory () {
  try {
    const raw = localStorage.getItem(ADDRESS_HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter((item) => typeof item === 'string' && item.trim()).slice(0, ADDRESS_HISTORY_LIMIT)
      : [];
  } catch (e) {
    return [];
  }
}

function saveAddressHistory (items) {
  try {
    localStorage.setItem(ADDRESS_HISTORY_KEY, JSON.stringify(items.slice(0, ADDRESS_HISTORY_LIMIT)));
  } catch (e) { /* ignore */ }
}

export default {
  name: 'MassOpenDoorSoundFilters',
  props: {
    value: {
      type: Object,
      required: true,
    },
    managementCompanies: {
      type: Array,
      default: () => [],
    },
    managementsGroup: {
      type: Array,
      default: () => [],
    },
    agents: {
      type: Array,
      default: () => [],
    },
    regions: {
      type: Array,
      default: () => [],
    },
    models: {
      type: Array,
      default: () => [],
    },
    mtoSectors: {
      type: Array,
      default: () => [],
    },
    mtoSectorsLoading: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      addressHistory: loadAddressHistory(),
      typeItems: [
        { value: 'M', text: 'Домофон' },
        { value: 'G', text: 'Калитка' },
        { value: 'A', text: 'Доп. вход' },
      ],
      physicalStatusItems: [
        { value: 1, text: 'Установлен' },
        { value: 2, text: 'Не установлен' },
        { value: 3, text: 'Без интернет соединения' },
        { value: 4, text: 'Пусконаладка' },
        { value: 5, text: 'Старый домофон' },
        { value: 6, text: 'Ожидает монтажа' },
      ],
    };
  },
  computed: {
    local: {
      get () {
        return this.value;
      },
      set (val) {
        this.$emit('input', val);
      },
    },
    addressSuggestions () {
      const query = String(this.local.address || '').trim().toLowerCase();
      if (!query) {
        return this.addressHistory;
      }
      return this.addressHistory.filter((item) => item.toLowerCase().includes(query));
    },
  },
  methods: {
    rememberAddress () {
      const address = String(this.local.address || '').trim();
      if (!address) return;

      const next = [
        address,
        ...this.addressHistory.filter((item) => item.toLowerCase() !== address.toLowerCase()),
      ].slice(0, ADDRESS_HISTORY_LIMIT);

      this.addressHistory = next;
      saveAddressHistory(next);
    },
    onAddressSearchInput (value) {
      if (value == null) return;
      this.local.address = value;
    },
    onSearch () {
      this.$nextTick(() => {
        this.rememberAddress();
        this.$emit('search');
      });
    },
    clearBbox () {
      this.local.bbox = null;
      this.$nextTick(() => this.$emit('search'));
    },
    reset () {
      this.$emit('input', {
        id: '',
        address: '',
        management_company_id: null,
        management_group_id: null,
        agent_id: null,
        region_id: [],
        mto_sector_id: [],
        type: null,
        model_id: null,
        physical_status: null,
        bbox: null,
      });
      this.$nextTick(() => this.$emit('reset'));
    },
  },
};
</script>

<style scoped>
.mods-filters {
  padding: 16px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8eef5;
  box-shadow: 0 2px 10px rgba(13, 64, 146, 0.05);
  height: 100%;
}
.mods-filters__head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.mods-filters__icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  background: linear-gradient(135deg, #0d4092 0%, #1e88e5 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.mods-filters__title {
  font-weight: 650;
  font-size: 0.95rem;
  color: #1a237e;
  line-height: 1.2;
}
.mods-filters__subtitle {
  font-size: 0.72rem;
  color: #78909c;
}
.mods-filters__section {
  margin-bottom: 8px;
}
.mods-filters__section-label {
  display: flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #90a4ae;
  margin-bottom: 8px;
}
</style>
